import {get, isEmpty, negate} from 'lodash/fp';
import {createSelector} from 'reselect';

import {BUSINESS_COACH, BUSINESS_LEADER} from '/enum/competency.enum';
import {sameDayLastMonthById} from '/redux/selector/coach.selector';
import {percentDifference} from '/util/number.util';
import {fromProps} from '/util/selector.util';

import {metric as metricState} from './root.selector';

export const teamsMetrics = createSelector(metricState, get('allTeams'));

export const teamsMetricsById = createSelector(
  fromProps(get('coachId')),
  teamsMetrics,
  (coachId, metrics) => {
    return get(coachId)(metrics);
  },
);

export const hasTeamsMetricsById = createSelector(
  teamsMetricsById,
  negate(isEmpty),
);

// ============ coaches ==============

export const coachesRankUpById = createSelector(
  teamsMetricsById,
  get('coaches.rankUpCount'),
);

export const coachesEarnedClientAcquisitionById = createSelector(
  teamsMetricsById,
  get('coaches.earnedClientAcquisitionBonusCount'),
);

export const coachesEarnedQuickStartBonusById = createSelector(
  teamsMetricsById,
  get('coaches.earnedQuickStartBonusCount'),
);

export const newFrontlineById = createSelector(
  teamsMetricsById,
  get('coaches.newFrontlineCoachesCount'),
);

export const allFrontlineById = createSelector(
  teamsMetricsById,
  get('coaches.frontlineCoachesCount'),
);

export const frontlineById = createSelector(
  teamsMetricsById,
  get('coaches.frontlineCoachesCount'),
);

export const newGeneration0ById = createSelector(
  teamsMetricsById,
  get('coaches.newGen0CoachesCount'),
);

export const allGeneration0ById = createSelector(
  teamsMetricsById,
  get('coaches.gen0CoachesCount'),
);

export const newOrganizationById = createSelector(
  teamsMetricsById,
  get('coaches.newOrganizationCoachesCount'),
);

export const allOrganizationById = createSelector(
  teamsMetricsById,
  get('coaches.organizationCoachesCount'),
);
export const myTeamsById = createSelector(
  teamsMetricsById,
  get('coaches.myTeamsCount'),
);
export const viewTeamsById = createSelector(
  teamsMetricsById,
  get('coaches.myTeamsCount'),
);
// ============ teams ==============

export const teamsSeniorCoachById = createSelector(
  teamsMetricsById,
  get('teams.seniorCoachTeamsCount'),
);

export const teamsExecutiveDirectorById = createSelector(
  teamsMetricsById,
  get('teams.executiveDirectorTeamsCount'),
);

export const teamsFibcById = createSelector(
  teamsMetricsById,
  get('teams.fibcTeamsCount'),
);

export const teamsFiblById = createSelector(
  teamsMetricsById,
  get('teams.fiblTeamsCount'),
);

export const currentVolumeGen0ById = createSelector(
  teamsMetricsById,
  get('teams.gen0Volume.current'),
);

export const projectedVolumeGen0ById = createSelector(
  teamsMetricsById,
  get('teams.gen0Volume.projected'),
);

export const sameDayLastMonthVolumeGen0ById = createSelector(
  sameDayLastMonthById,
  get('gen0Vol'),
);

export const currentTotalVolumeGenerationById = createSelector(
  teamsMetricsById,
  get('teams.totalGenerationVolume.current'),
);

export const projectedTotalVolumeGenerationById = createSelector(
  teamsMetricsById,
  get('teams.totalGenerationVolume.projected'),
);

export const sameDayLastMonthTotalVolumeGenerationById = createSelector(
  sameDayLastMonthById,
  get('totalVol'),
);

export const currentVolumeFrontlineById = createSelector(
  teamsMetricsById,
  get('teams.frontlineQualifyingVolume.current'),
);

export const projectedVolumeFrontlineById = createSelector(
  teamsMetricsById,
  get('teams.frontlineQualifyingVolume.projected'),
);

export const sameDayLastMonthVolumeFrontlineById = createSelector(
  sameDayLastMonthById,
  get('frontlineVol'),
);

// ============ rank ==============

export const performanceAboveRankById = createSelector(
  teamsMetricsById,
  get('rankPerformance.aboveRankCount'),
);

export const performanceAtRankById = createSelector(
  teamsMetricsById,
  get('rankPerformance.atRankCount'),
);

export const performanceBelowRankById = createSelector(
  teamsMetricsById,
  get('rankPerformance.belowRankCount'),
);

export const closeToCompetencySeniorCoachById = createSelector(
  teamsMetricsById,
  get('closeToRank.seniorCoachCount'),
);

export const closeToCompetencyExecutiveDirectorById = createSelector(
  teamsMetricsById,
  get('closeToRank.executiveDirectorCount'),
);

export const closeToCompetencyFibcById = createSelector(
  teamsMetricsById,
  get('closeToRank.fibcCount'),
);

// ============ bonuses/at risk ==============

export const bonusEligibleForCabById = createSelector(
  teamsMetricsById,
  get('bonuses.eligibleForCabCount'),
);

export const bonusEligibleForAccelById = createSelector(
  teamsMetricsById,
  get('bonuses.eligibleForAccelBonusCount'),
);
export const bonusEarnedAccelById = createSelector(
  teamsMetricsById,
  get('coaches.earnedAccelBonusCount'),
);
export const bonusEarnedClientSupportPlusById = createSelector(
  teamsMetricsById,
  get('coaches.earnedClientSupportBonusPlusCount'),
);

export const bonusQualifiedForIncentiveTripById = createSelector(
  teamsMetricsById,
  get('coaches.qualifiedForIncentiveTripCount'),
);
export const bonusEligibleForQsbById = createSelector(
  teamsMetricsById,
  get('bonuses.eligibleForQsbCount'),
);

export const atRiskNotCertifiedById = createSelector(
  teamsMetricsById,
  get('atRisk.activeNotCertifiedCount'),
);

export const atRiskExpiringCoachRenewalsById = createSelector(
  teamsMetricsById,
  get('atRisk.expiringCoachRenewalsCount'),
);

export const oneToTwoCSBPClientCountById = createSelector(
  teamsMetricsById,
  get('bonuses.oneToTwoCSBPClientCount'),
);

export const threeToFourCSBPClientCountById = createSelector(
  teamsMetricsById,
  get('bonuses.threeToFourCSBPClientCount'),
);

export const fiveToSixCSBPClientCountById = createSelector(
  teamsMetricsById,
  get('bonuses.fiveToSixCSBPClientCount'),
);

export const sevenToNineCSBPClientCountById = createSelector(
  teamsMetricsById,
  get('bonuses.sevenToNineCSBPClientCount'),
);

export const bonusEligibleForClientSupportPlusById = createSelector(
  teamsMetricsById,
  get('bonuses.eligibleClientSupportBonusPlusCount'),
);

export const eligibleForQsbCountById = createSelector(
  teamsMetricsById,
  get('bonuses.eligibleForQsbCount'),
);

export const bonusEligibleForIncentiveTripById = createSelector(
  teamsMetricsById,
  get('bonuses.eligibleForIncentiveTripCount'),
);
// ============ coaches summary ==============

export const coachesTeamsSummaryById = createSelector(
  newFrontlineById,
  newGeneration0ById,
  newOrganizationById,
  teamsSeniorCoachById,
  teamsExecutiveDirectorById,
  teamsFibcById,
  (
    newFrontlineCoaches,
    newGen0Coaches,
    newOrganizationCoaches,
    teamsSeniorCoach,
    teamsExecutiveDirector,
    teamsFibc,
  ) => {
    return {
      [BUSINESS_COACH]: {
        newFrontline: newFrontlineCoaches,
        newGen0: newGen0Coaches,
        teamsSeniorCoach,
        teamsExecutiveDirector,
      },
      [BUSINESS_LEADER]: {
        newFrontline: newFrontlineCoaches,
        newGen0: newGen0Coaches,
        newOrganization: newOrganizationCoaches,
        teamsSeniorCoach,
        teamsExecutiveDirector,
        teamsFibc,
      },
    };
  },
);

export const coachesTeamsSummaryByIdByCompetency = createSelector(
  fromProps(get('competency')),
  coachesTeamsSummaryById,
  (competency, volumeSummary) => {
    return get(competency)(volumeSummary);
  },
);

// ============ teams volumes summary ==============

export const deltaVolumeGen0ById = createSelector(
  currentVolumeGen0ById,
  sameDayLastMonthVolumeGen0ById,
  percentDifference,
);

export const deltaVolumeFrontlineById = createSelector(
  currentVolumeFrontlineById,
  sameDayLastMonthVolumeFrontlineById,
  percentDifference,
);

export const deltaVolumeGenerationById = createSelector(
  currentTotalVolumeGenerationById,
  sameDayLastMonthTotalVolumeGenerationById,
  percentDifference,
);

export const teamsVolumesSummaryById = createSelector(
  currentVolumeGen0ById,
  projectedVolumeGen0ById,
  sameDayLastMonthVolumeGen0ById,
  currentTotalVolumeGenerationById,
  projectedTotalVolumeGenerationById,
  sameDayLastMonthTotalVolumeGenerationById,
  currentVolumeFrontlineById,
  projectedVolumeFrontlineById,
  sameDayLastMonthVolumeFrontlineById,
  deltaVolumeGen0ById,
  deltaVolumeFrontlineById,
  deltaVolumeGenerationById,
  (
    currentVolumeGen0,
    projectedVolumeGen0,
    lastVolumeGen0,
    currentTotalVolumeGeneration,
    projectedTotalVolumeGeneration,
    lastTotalVolumeGeneration,
    currentVolumeFrontline,
    projectedVolumeFrontline,
    lastVolumeFrontline,
    deltaVolumeGen0,
    deltaVolumeFrontline,
    deltaVolumeGeneration,
  ) => {
    return {
      [BUSINESS_COACH]: {
        currentVolume: currentVolumeFrontline,
        projectedVolume: projectedVolumeFrontline,
        sdlmVolume: lastVolumeFrontline,
        sdlmPrimaryDelta: deltaVolumeFrontline,
        totalVolume: currentVolumeGen0,
        projectedGenVolume: projectedVolumeGen0,
        sdlmGenVolume: lastVolumeGen0,
        sdlmSecondaryDelta: deltaVolumeGen0,
      },
      [BUSINESS_LEADER]: {
        currentVolume: currentVolumeGen0,
        projectedVolume: projectedVolumeGen0,
        sdlmVolume: lastVolumeGen0,
        sdlmPrimaryDelta: deltaVolumeGen0,
        totalVolume: currentTotalVolumeGeneration,
        projectedGenVolume: projectedTotalVolumeGeneration,
        sdlmGenVolume: lastTotalVolumeGeneration,
        sdlmSecondaryDelta: deltaVolumeGeneration,
      },
    };
  },
);

export const teamsVolumesSummaryByIdByCompetency = createSelector(
  fromProps(get('competency')),
  teamsVolumesSummaryById,
  (competency, volumeSummary) => {
    return get(competency)(volumeSummary);
  },
);
